import logo from "assets/images/nfpa-link-glyph-reversed.png";
import Anchor from "atoms/anchors/anchor";
import Button from "atoms/buttons/button";
import { ButtonStyles } from "atoms/constants/button-styles";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import { ClassNameConstants } from "constants/classname-constants";
import { siteMap } from "internal-sitemap";
import { CloseDialogEvent } from "molecules/modals/modal";
import OfflineAlertModal from "organisms/modals/offline-alert-modal/offline-alert-modal";
import SearchModal from "organisms/modals/search-modal/search-modal";
import ProfileMenu from "organisms/profile/profile-menu";
import SidebarNavigationTooltip from "organisms/sidebar-navigation/sidebar-navigation-tooltip";
import * as React from "react";
import { useCallback, useRef, useState } from "react";
import useNetworkInformation from "utilities/contexts/network-information/use-network-information";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import useIsNestedRoute from "utilities/hooks/routing/use-is-nested-route";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import { useLocalization } from "utilities/hooks/use-localization";
import useModalActions from "utilities/hooks/use-modal-actions";
import AllowRoles from "molecules/allow-roles/allow-roles";
import { AdminRoleTypes } from "utilities/enumerations/role-type";
import {
    SearchEventsProvider,
    SearchSource,
} from "../../utilities/contexts/search-events/search-events-provider";
import ChatModal from "organisms/modals/chat-modal/chat-modal";
import MobileNavigation from "organisms/sidebar-navigation/mobile-navigation";
import useBreakpointRanges from "utilities/hooks/use-breakpoint-ranges";
import useAiChatEnabled from "utilities/hooks/use-ai-chat-enabled";
import useNotebooksEnabled from "utilities/hooks/use-notebooks-enabled";
import CasiUserAgreementModal from "organisms/modals/casi-user-agreement-modal/casi-user-agreement-modal";
import {
    useChatModal,
    useSyncOpenStateToAtom,
} from "utilities/hooks/domain/chat/use-chat-modal";
import CultureResources from "utilities/interfaces/culture-resources";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface SidebarNavigationProps {}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const CSS_BASE_CLASSNAME = "c-sidebar-navigation";
const { Hover } = ClassNameConstants;
const TOOLTIP_DISTANCE = 20;
const TOOLTIP_SKIDDING = 0;

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const SidebarNavigation: React.FunctionComponent<
    SidebarNavigationProps
> = () => {
    const { routeIsAdmin, routeIsSitNav, routeIsMyLink, routeIsNotebooks } =
        useIsNestedRoute();
    const { t } = useLocalization();
    const { isOnline } = useNetworkInformation();

    const enableNotebooks = useNotebooksEnabled();
    const { enableAIChat, useAIChat } = useAiChatEnabled();
    const showAIChatButton = useAIChat;
    const offlineDisabledClass = !isOnline ? "-disabled" : "";
    const [mobileNavMenuOpen, setMobileNavMenuOpen] = useState(false);

    const {
        isOpen: isOfflineAlertModalOpen,
        handleClose: handleOfflineAlertModalClose,
        handleOpen: handleFeatureUnavailableModalOpen,
    } = useModalActions();
    const {
        isOpen: isCasiAgreementModalOpen,
        handleClose: handleCasiAgreementModalClose,
        handleOpen: handleCasiAgreementModalOpen,
    } = useModalActions();

    const isMobile = useBreakpoint(
        Breakpoints.Tablet,
        BreakpointComparer.MaxWidth
    );

    const { inMobileView: showMobileNav } = useBreakpointRanges();
    // Used to temporarily turn off tooltips for the search button so that it does not steal focus
    // when navigating via mouse or link
    const [searchTooltipDisabled, setSearchTooltipDisabled] =
        useState<boolean>(false);

    const [chatTooltipDisabled, setChatTooltipDisabled] =
        useState<boolean>(false);

    const [showSearch, setShowSearch] = useState<boolean>(false);

    useSyncOpenStateToAtom(); // Syncs CASI open state to URL params
    const {
        closeChatModal,
        openChatModal,
        isOpen: isChatModalOpen,
    } = useChatModal();

    const searchRef = useRef<HTMLButtonElement>(null);
    const chatRef = useRef<HTMLButtonElement>(null);
    const sideNavigationRef = useRef<HTMLElement>(null);

    const applyHoverIfActive = (className: string, routeIsActive: boolean) => {
        if (!routeIsActive || showSearch || isChatModalOpen) {
            return className;
        }

        return `${className} ${Hover}`;
    };

    const shouldCloseSearch = (target: EventTarget) =>
        showSearch &&
        target instanceof Element &&
        sideNavigationRef?.current?.contains(target);

    // -----------------------------------------------------------------------------------------
    // #region Event Handlers
    // -----------------------------------------------------------------------------------------

    const handleClickSidebarNavigation = (
        event: React.MouseEvent<HTMLElement, MouseEvent>
    ) => {
        if (shouldCloseSearch(event.target)) {
            setSearchTooltipDisabled(true);
            handleCloseSearch();
        }
    };

    const handleCloseSearch = (event?: CloseDialogEvent) => {
        if (isMouseClickEvent(event) || isClickedLink(event)) {
            setSearchTooltipDisabled(true);
        }

        setShowSearch(false);
    };

    const handleCloseChat = (event?: CloseDialogEvent) => {
        if (isMouseClickEvent(event) || isClickedLink(event)) {
            setChatTooltipDisabled(true);
        }

        closeChatModal();

        if (showMobileNav && mobileNavMenuOpen) {
            setMobileNavMenuOpen(false);
        }
    };

    const handleCloseSearchComplete = () => {
        setSearchTooltipDisabled(false);
    };

    const handleCloseChatComplete = () => {
        setChatTooltipDisabled(false);
    };

    const handleShowSearch = () => {
        if (!showSearch) {
            closeChatModal();
            setShowSearch(true);
        }
        if (showSearch) {
            setShowSearch(false);
        }

        if (showMobileNav && mobileNavMenuOpen) {
            setMobileNavMenuOpen(false);
        }
    };

    const handleShowChat = useCallback(() => {
        if (!isChatModalOpen) {
            setShowSearch(false);
        }

        if (useAIChat && !enableAIChat) {
            handleCasiAgreementModalOpen();
            return;
        }

        isChatModalOpen ? closeChatModal() : openChatModal();

        if (showMobileNav && mobileNavMenuOpen) {
            setMobileNavMenuOpen(false);
        }
    }, [
        closeChatModal,
        useAIChat,
        enableAIChat,
        handleCasiAgreementModalOpen,
        isChatModalOpen,
        mobileNavMenuOpen,
        openChatModal,
        showMobileNav,
    ]);

    const handleCloseCASIAgreement = () => {
        handleCasiAgreementModalClose();

        if (showMobileNav && mobileNavMenuOpen) {
            setMobileNavMenuOpen(false);
        }
    };

    const handleLinkClick = (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
    ) => {
        if (isChatModalOpen) {
            handleCloseChat();
        }

        if (showMobileNav && mobileNavMenuOpen) {
            setMobileNavMenuOpen(false);
        }

        if (isOnline) {
            return true;
        }

        e.preventDefault();

        if (isMobile) {
            handleFeatureUnavailableModalOpen();
        }
        return;
    };

    const showOfflineTooltip = (tooltip: keyof CultureResources | string) => {
        tooltip = tooltip as keyof CultureResources;
        return !isOnline ? t("offline-fallback-description") : tooltip;
    };

    // #endregion Event Handlers

    // -----------------------------------------------------------------------------------------
    // #region Render
    // -----------------------------------------------------------------------------------------

    return (
        <aside
            className={CSS_BASE_CLASSNAME}
            onClickCapture={handleClickSidebarNavigation}
            ref={sideNavigationRef}>
            <div className={`${CSS_BASE_CLASSNAME}__logo`}>
                <SidebarNavigationTooltip
                    description={t("nav-tooltip-home-description")}
                    offsetInPx={{
                        distance: TOOLTIP_DISTANCE,
                        skidding: TOOLTIP_SKIDDING,
                    }}
                    title={t("nav-tooltip-home-title")}>
                    <Anchor
                        dataTestId="sidebar-navigation--home-anchor"
                        to={siteMap.dashboards.user}
                        ariaLabel={t("nav-tooltip-home-title")}
                        onClick={handleCloseChat}>
                        <img src={logo} alt={t("nav-tooltip-home-title")} />
                    </Anchor>
                </SidebarNavigationTooltip>
            </div>
            {showMobileNav ? (
                <MobileNavigation
                    handleLinkClick={handleLinkClick}
                    handleShowSearch={handleShowSearch}
                    handleShowChat={handleShowChat}
                    handleCloseChat={handleCloseChat}
                    isOpen={mobileNavMenuOpen}
                    setIsOpen={setMobileNavMenuOpen}
                />
            ) : (
                <>
                    {enableNotebooks && (
                        // TODO: Update the Anchor to navigate to last used notebook
                        <SidebarNavigationTooltip
                            description={showOfflineTooltip(
                                t("nav-tooltip-notebooks-description")
                            )}
                            title={t("notebooks")}>
                            <Anchor
                                dataTestId="sidebar-navigation--notebooks-anchor"
                                ariaLabel={t("notebooks")}
                                cssClassName={applyHoverIfActive(
                                    `${offlineDisabledClass} c-button -icon`,
                                    routeIsNotebooks
                                )}
                                to={`${siteMap.notebooks.list}`}
                                onClick={handleLinkClick}>
                                <Icon
                                    type={Icons.Notebook}
                                    size={IconSizes.Large}
                                />
                                <span className="sr-only">
                                    {t("nav-aria-notebook-link")}
                                </span>
                            </Anchor>
                        </SidebarNavigationTooltip>
                    )}
                    <SidebarNavigationTooltip
                        description={showOfflineTooltip(
                            t("nav-tooltip-direct-description")
                        )}
                        title={t("nav-tooltip-direct-title")}>
                        <Anchor
                            ariaLabel={t("nav-aria-direct-link")}
                            cssClassName={applyHoverIfActive(
                                `${offlineDisabledClass} c-button -icon`,
                                routeIsSitNav
                            )}
                            dataTestId="sitnav-link"
                            to={siteMap.situationalNavigation.dashboard}
                            onClick={handleLinkClick}>
                            <Icon type={Icons.Sitnav} size={IconSizes.Large} />
                            <span className="sr-only">
                                {t("nav-aria-direct-link")}
                            </span>
                        </Anchor>
                    </SidebarNavigationTooltip>
                    <SidebarNavigationTooltip
                        description={showOfflineTooltip(
                            t("nav-tooltip-mylink-description")
                        )}
                        title={t("nav-tooltip-mylink-title")}>
                        <Anchor
                            dataTestId="sidebar-navigation--my-link-anchor"
                            ariaLabel={t("nav-aria-mylink-link")}
                            cssClassName={applyHoverIfActive(
                                `${offlineDisabledClass} c-button -icon`,
                                routeIsMyLink
                            )}
                            to={siteMap.myLink.myBookmarks.dashboard}
                            onClick={handleLinkClick}>
                            <Icon
                                type={Icons.BookmarkOutline}
                                size={IconSizes.Large}
                            />
                            <span className="sr-only">
                                {t("nav-aria-mylink-link")}
                            </span>
                        </Anchor>
                    </SidebarNavigationTooltip>
                    <SidebarNavigationTooltip
                        description={t("nav-tooltip-search-description")}
                        disabled={searchTooltipDisabled}
                        title={t("nav-tooltip-search-title")}>
                        <Button
                            dataTestId="sidebar-navigation--search-button"
                            accessibleText={t("nav-aria-search-link")}
                            cssClassName={`-icon ${showSearch ? Hover : ""}`}
                            onClick={handleShowSearch}
                            ref={searchRef}
                            style={ButtonStyles.Primary}>
                            <Icon type={Icons.Search} size={IconSizes.Large} />
                        </Button>
                    </SidebarNavigationTooltip>
                    {showAIChatButton && (
                        <SidebarNavigationTooltip
                            description={showOfflineTooltip(
                                t("nav-tooltip-chat-description")
                            )}
                            disabled={chatTooltipDisabled}
                            title={t("casi")}>
                            <Button
                                dataTestId="sidebar-navigation--casi-button"
                                accessibleText={t("nav-aria-chat-link")}
                                cssClassName={applyHoverIfActive(
                                    `c-button -icon`,
                                    false
                                )}
                                onClick={handleShowChat}
                                ref={chatRef}
                                style={ButtonStyles.Primary}>
                                <Icon
                                    type={Icons.Casi}
                                    size={IconSizes.Large}
                                    cssClassName="-casi"
                                />
                            </Button>
                        </SidebarNavigationTooltip>
                    )}
                    <AllowRoles allowedRoles={AdminRoleTypes}>
                        <Anchor
                            dataTestId="sidebar-navigation--admin-anchor"
                            ariaLabel="Admin Dashboard"
                            cssClassName={applyHoverIfActive(
                                "c-button -icon",
                                routeIsAdmin
                            )}
                            to={siteMap.admin.dashboard}
                            onClick={handleCloseChat}>
                            <Icon
                                type={Icons.Settings}
                                size={IconSizes.Large}
                            />
                            <span className="sr-only">
                                Go to Admin Dashboard
                            </span>
                        </Anchor>
                    </AllowRoles>
                </>
            )}
            <ProfileMenu handleCloseChat={handleCloseChat} />
            <SearchEventsProvider source={SearchSource.Sidenav}>
                <SearchModal
                    closeDialog={handleCloseSearch}
                    isVisible={showSearch}
                    onCloseComplete={handleCloseSearchComplete}
                />
            </SearchEventsProvider>

            <ChatModal
                closeDialog={handleCloseChat}
                isVisible={isChatModalOpen}
                onCloseComplete={handleCloseChatComplete}
            />

            {isOfflineAlertModalOpen && (
                <OfflineAlertModal
                    isOpen={isOfflineAlertModalOpen}
                    handleClose={handleOfflineAlertModalClose}
                />
            )}

            <CasiUserAgreementModal
                isOpen={isCasiAgreementModalOpen}
                onClose={handleCloseCASIAgreement}
            />
        </aside>
    );

    // #endregion Render
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Private Functions
// -----------------------------------------------------------------------------------------

const isMouseClickEvent = (event?: any) =>
    event != null &&
    event.clientX !== 0 &&
    event.clientY !== 0 &&
    event.nativeEvent instanceof MouseEvent;

const isClickedLink = (event?: CloseDialogEvent) =>
    event?.currentTarget?.tagName === "A";

// #endregion Private Functions

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default SidebarNavigation;

// #endregion Exports
